import authService, { type User } from "@/lib/auth";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

interface LoginResult {
  success: boolean;
  error?: string;
  requires2FA?: true;
  tempToken?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  loginWithToken: (token: string) => void;
  verify2FA: (
    token: string,
    tempToken: string,
  ) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      if (authService.isAuthenticated()) {
        try {
          const response = await authService.me();
          if (response.data) {
            setUser(response.data.user);
          } else {
            // Token invalid, try to refresh
            const refreshResponse = await authService.refreshToken();
            if (refreshResponse.data) {
              setUser(refreshResponse.data.user);
            }
          }
        } catch {
          // Auth failed, clear token
          authService.setToken(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (
    email: string,
    password: string,
  ): Promise<LoginResult> => {
    const response = await authService.login(email, password);

    if (response.data) {
      // Check if 2FA is required
      if ("requires2FA" in response.data && response.data.requires2FA) {
        return {
          success: false,
          requires2FA: true,
          tempToken: response.data.tempToken,
        };
      }

      // Normal login success
      if ("user" in response.data) {
        setUser(response.data.user);
        return { success: true };
      }
    }

    return {
      success: false,
      error: response.message || "Erro ao fazer login",
    };
  };

  const verify2FA = async (
    token: string,
    tempToken: string,
  ): Promise<{ success: boolean; error?: string }> => {
    const response = await authService.verify2FA(token, tempToken);

    if (response.data) {
      setUser(response.data.user);
      return { success: true };
    }

    return {
      success: false,
      error: response.message || "Código inválido",
    };
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const refreshUser = async () => {
    const response = await authService.me();
    if (response.data) {
      setUser(response.data.user);
    }
  };

  const loginWithToken = (token: string) => {
    authService.loginWithToken(token);
    // Fetch user data after setting token
    authService.me().then((response) => {
      if (response.data) {
        setUser(response.data.user);
      }
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        loginWithToken,
        verify2FA,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
